import React, { useState, useEffect, useMemo, useRef } from 'react';
import { getSymbols, getTestnetAvailability } from '../../services/SymbolsService';
import { processError } from '../../services/ServiceUtils';
import { getEventObject } from '../../utils/EventUtils';
import { getCurrentEnvironment } from '../../utils/CurrentEnvironmentStorage';
import Select from 'react-select/creatable';
// import { getQuoteFromWildcard } from '../../utils/SymbolUtils';

// Opção "Todos / Coringa": value vazio → o backend não filtra por símbolo e retorna
// TUDO, inclusive estratégias coringa (*USDT, *BTC) que não casam um símbolo concreto.
const ALL_OPTION = { label: 'Todos / Coringa', value: '' };

/**
 * props:
 * - symbol
 * - disabled
 * - onChange
 * - hideSelectOption
 * - includeAllOption  // adiciona "Todos / Coringa" (value '') no topo da lista
 */
function SelectSymbol(props) {

    const [symbols, setSymbols] = useState(['LOADING']);
    const [isDisabled, setIsDisabled] = useState(false);
    const [value, setValue] = useState('');
    // Disponibilidade na TESTNET: Set de símbolos que existem lá, ou null quando não há
    // informação (produção — nunca busca; ou fail-open do backend/fetch em curso). null = não
    // marca nada, comportamento idêntico ao de hoje. Guardamos os nomes crus num ref pra poder
    // reconstruir as opções quando a disponibilidade chegar DEPOIS da lista de símbolos.
    const [testnetAvailable, setTestnetAvailable] = useState(null);
    const rawSymbolNamesRef = useRef(null);

    useEffect(() => {
        setValue(getSelectObject(props.symbol));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.symbol, testnetAvailable]);

    useEffect(() => {
        setIsDisabled(props.disabled);
    }, [props.disabled]);

    useEffect(() => {
        getSymbols()
            .then(symbolObjects => {
                const symbolNames = symbolObjects.rows ? symbolObjects.rows.map(s => s.symbol) : symbolObjects.map(s => s.symbol);

                if (symbolNames.length) {
                    rawSymbolNamesRef.current = symbolNames;
                    const opts = symbolNames.map(s => getSelectObject(s));
                    setSymbols(props.includeAllOption ? [ALL_OPTION, ...opts] : opts);
                    // props.onChange(getEventObject('symbol', symbolNames[0])); // ta trocando o symbol inicial do SelectSymbol
                }
                else {
                    setSymbols(["NO SYMBOLS"]);
                    setIsDisabled(true);
                }
            })
            .catch(err => {
                processError(err);
                setSymbols(["ERROR"]);
                setIsDisabled(true);
            })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Disponibilidade na testnet (dropdown ciente da testnet, 14/07): só busca quando o
    // ambiente ATUAL é 'test' — em produção NENHUMA chamada nova é feita (o sync já reflete a
    // realidade). Fail-open: erro ou `available: null` do backend → estado permanece null.
    useEffect(() => {
        if (getCurrentEnvironment() !== 'test') return;
        getTestnetAvailability()
            .then(r => { if (r && Array.isArray(r.available)) setTestnetAvailable(new Set(r.available)); })
            .catch(() => { /* fail-open: mantém null */ });
    }, []);

    // Quando a disponibilidade chega DEPOIS da lista de símbolos já carregada, reconstrói as
    // opções pra aplicar a marcação (evita corrida entre os dois fetches independentes).
    useEffect(() => {
        if (!testnetAvailable || !rawSymbolNamesRef.current) return;
        const opts = rawSymbolNamesRef.current.map(s => getSelectObject(s));
        setSymbols(props.includeAllOption ? [ALL_OPTION, ...opts] : opts);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [testnetAvailable]);

    function getSelectObject(itemValue) {
        if (props.includeAllOption && (itemValue === '' || itemValue === undefined || itemValue === null))
            return ALL_OPTION;
        const unavailable = !!(testnetAvailable && itemValue && !testnetAvailable.has(itemValue));
        return unavailable
            ? { label: `${itemValue} — indisponível na testnet`, value: itemValue, isDisabled: true }
            : { label: itemValue, value: itemValue };
    }

    function onSymbolChange(event) {
        props.onChange(getEventObject('symbol', event.value));
    }

    const customStyles = {
        control: (provided) => ({
            ...provided,
            width: 220
        }),
        menuPortal: base => ({ ...base, zIndex: 9999 })
    };

    const selectSymbol = useMemo(() => {
        return (
            <Select
                value={value}
                isDisabled={isDisabled}
                isOptionDisabled={(option) => !!option.isDisabled}
                styles={customStyles}
                onChange={onSymbolChange}
                options={symbols}
                menuPortalTarget={document.body}
            />
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [symbols, value, isDisabled]);

    return (selectSymbol)
}

export default SelectSymbol;